import React from 'react';
import { Award, MapPin, Calendar, Briefcase } from 'lucide-react';

const About = () => {
  const highlights = [
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "6+ Years Experience",
      description: "Comprehensive IT expertise across development, testing, and deployment"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Innovation Awards",
      description: "Won multiple awards for innovative ideas and project excellence"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Global Impact",
      description: "Collaborated with clients across Romania, Canada, Ireland, and Indonesia"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Problem Solver",
      description: "90% reduction in manual resolution time through automation"
    }
  ];

  return (
    <section id="about" className="py-12 sm:py-16 lg:py-20 bg-slate-800/50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            About <span className="text-blue-400">Me</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-6 sm:mb-8"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white mb-6">
              Passionate About Building <span className="text-blue-400">Tech Solutions</span>
            </h3>
            
            <div className="space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>
                I completed my B.Tech in Electronics and Communication Engineering from National Institute of Science and Technology, 
                Berhampur, Odisha in 2017, and started my career in IT through campus placement. I have a deep interest in building 
                tech solutions that positively impact others.
              </p>
              
              <p>
                Outside of work, I enjoy traveling and trekking—it recharges my energy and offers valuable life lessons. 
                Trekking has taught me that progress comes one step at a time, and that the journey often holds more value 
                than the destination. This mindset has had a significant influence on how I approach challenges and problem-solving.
              </p>
              
              <p>
                Over the past 7 years of my professional journey, I've tackled various technical challenges. One of the most 
                memorable was resolving a compliance issue in a payment module, which required integrating five different 
                applications—a complex task that helped sharpen my tech knowledge and coordination skills.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
              <h4 className="text-lg sm:text-xl font-semibold text-blue-400 mb-3">Current Focus</h4>
              <p className="text-gray-300 text-sm sm:text-base">
                Currently focusing on leveraging Generative AI to solve real-world challenges and boost productivity through innovative, AI-first approaches. 
                I'm actively exploring and learning LLMs, prompt engineering, and cutting-edge AI tools, with the goal of applying them to create scalable, 
                impactful solutions across domains. My focus is on blending strategic thinking with hands-on AI experimentation to drive efficiency, 
                creativity, and meaningful business outcomes.
              </p>
            </div>
          </div>

          {/* Right Content - Highlights */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4 sm:p-6 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10"
              >
                <div className="text-blue-400 mb-4">
                  {highlight.icon}
                </div>
                <h4 className="text-base sm:text-lg font-semibold text-white mb-2">
                  {highlight.title}
                </h4>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Education Section */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 text-center">Education</h3>
          <div className="max-w-3xl mx-auto">
            <div className="bg-slate-700/30 rounded-lg p-4 sm:p-6 border border-blue-500/20">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h4 className="text-lg sm:text-xl font-semibold text-blue-400 mb-2">
                    B.Tech in Electronics & Communication Engineering
                  </h4>
                  <p className="text-gray-300 mb-2 text-sm sm:text-base">
                    National Institute of Science & Technology, Berhampur
                  </p>
                  <p className="text-gray-400 text-sm">BPUT, Odisha</p>
                </div>
                <div className="text-center md:text-right">
                  <span className="inline-block bg-blue-500/20 text-blue-400 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                    2017
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;